import { gql } from '@apollo/client';

import {
  Quote,
  Supplier,
  SupplierReview,
  SupplierRecommendation,
} from '../../types';
import { Attachment, QuoteLineItemsFields, Review } from './quoteForRequest';
import {
  priceBreakdown,
  quotePriceBreakdown,
} from '../../graphql/fragments/quotes/priceBreakdown';

type SupplierFields = Pick<
  Supplier,
  | 'uuid'
  | 'name'
  | 'slug'
  | 'tagline'
  | 'category_tier1'
  | 'average_rating'
  | 'commission'
  | 'images'
  | 'description'
  | 'reviewCount'
  | 'rating'
>;

export type ReviewFields = Pick<
  SupplierReview,
  'averageRating' | 'fieldPublicComment' | 'authenticated' | 'source'
>;

type QuoteFields = Pick<
  Quote,
  | 'uuid'
  | 'total'
  | 'covers'
  | 'supportText'
  | 'event_type'
  | 'userFirstName'
  | 'service_fee'
  | 'commission_fee'
  | 'old_commission'
  | 'agencyCommissionFee'
>;

type QuotePriceDetails = {
  affiliateCommissionAmount: number;
  affiliateCommissionRate: number;
  commissionFeeAmount: number;
  commissionFeeRate: number;
  created: string;
  customerTotalAmount: number;
  partnerCommissionAmount: number;
  partnerCommissionRate: number;
  protectPlusAmount: number;
  protectPlusRate: number;
  quoteTotalAmount: number;
  serviceFeeAmount: number;
  serviceFeeRate: number;
  supplierTakeHomeAmount: number;
  customerTotalPriceExclVat: number;
  combinedServiceFeeRate: number;
  combinedServiceFeeAmount: number;
  updated: string;
  vatAmount: number;
  uuid: string;
  quoteUuid: string;
};

export type PublicQuoteFields = QuoteFields & {
  quote_line_items: Array<QuoteLineItemsFields>;
  supplier: SupplierFields & {
    reccomendations: Array<SupplierRecommendation>;
    reviews: Array<Review>;
  };
  attachments: Attachment[];
  quotePriceDetails: QuotePriceDetails;
  introMessage: string;
};

export interface PublicQuote {
  publicQuote: PublicQuoteFields;
}

export const PUBLIC_QUOTE = gql`
  query publicQuoteInformation($quoteId: String!) {
    publicQuote(quote_id: $quoteId) {
      uuid
      total
      covers
      supportText
      event_type
      userFirstName
      service_fee
      commission_fee
      old_commission
      vatStatus
      agencyCommissionFee {
        commission
      }
      priceBreakdown {
        ...QuotePriceBreakdown
      }
      quote_line_items {
        title
        description
        quantity
        price
        order
        image {
          url
        }
        priceType
        priceBreakdown {
          ...PriceBreakdown
        }
      }
      supplier: suppliers {
        uuid
        name
        slug
        tagline
        category_tier1
        average_rating
        commission
        reviewCount
        images {
          url
        }
        description
        rating {
          overall
          friendliness
          professionalism
          quality
          valueForMoney
        }
        reviews {
          fieldPublicComment
          averageRating
          source
          authenticated
          reviewsExternalEvent {
            userFullName
            eventType
            date
          }
          quotes {
            event_requests {
              events {
                date
                type
                users {
                  firstName
                  lastName
                }
              }
            }
          }
        }
        recommendations {
          highlight
          recommendation
          team_member_name
        }
      }
      attachments {
        url
        key
        filename
      }
      quotePriceDetails {
        uuid
        quoteUuid
        supplierTakeHomeAmount
        commissionFeeRate
        commissionFeeAmount
        quoteTotalAmount
        serviceFeeRate
        serviceFeeAmount
        protectPlusRate
        protectPlusAmount
        partnerCommissionRate
        partnerCommissionAmount
        affiliateCommissionRate
        affiliateCommissionAmount
        customerTotalAmount
        customerTotalPriceExclVat
        combinedServiceFeeRate
        vatAmount
        created
        updated
      }
      introMessage
    }
  }
  ${quotePriceBreakdown}
  ${priceBreakdown}
`;
